import { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { MainLayout } from '@/layouts/MainLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ExternalRedirect } from '@/components/ExternalRedirect';
import { lazy } from 'react';

// Marketing site URL - configurable per environment
// Falls back to production URL if env var is undefined or empty
const MARKETING_SITE_URL =
  import.meta.env.VITE_MARKETING_SITE_URL?.trim().replace(/\/$/, '') || 'https://rescuefoodies.com';

// Core app pages
const BrowseListingsPage = lazy(() => import('@/pages/BrowseListingsPage'));
const MerchantDetailPage = lazy(() => import('@/pages/MerchantDetailPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('@/pages/OrderConfirmationPage'));
const OrdersPage = lazy(() => import('@/pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/pages/OrderDetailPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const LoginCallbackPage = lazy(() => import('@/pages/LoginCallbackPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));

const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    children: [
      // Root path → redirect to marketplace (Option A)
      {
        index: true,
        element: <Navigate to="/marketplace" replace />,
      },
      {
        path: 'marketplace',
        element: <BrowseListingsPage />,
      },
      // Alias for backwards compatibility
      {
        path: 'browse',
        element: <Navigate to="/marketplace" replace />,
      },
      {
        path: 'merchant/:id/:slug?',
        element: <MerchantDetailPage />,
      },
      {
        path: 'checkout',
        element: (
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'order-confirmation',
        element: (
          <ProtectedRoute>
            <OrderConfirmationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders',
        element: (
          <ProtectedRoute>
            <OrdersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/:id',
        element: (
          <ProtectedRoute>
            <OrderDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'login/callback',
        element: <LoginCallbackPage />,
      },
      {
        path: 'register',
        element: <RegisterPage />,
      },
      // Static pages → Redirect to marketing site
      {
        path: 'about',
        element: <ExternalRedirect to={`${MARKETING_SITE_URL}/about`} />,
      },
      {
        path: 'terms',
        element: <ExternalRedirect to={`${MARKETING_SITE_URL}/terms`} />,
      },
      {
        path: 'privacy',
        element: <ExternalRedirect to={`${MARKETING_SITE_URL}/privacy`} />,
      },
      {
        path: 'contact',
        element: <ExternalRedirect to={`${MARKETING_SITE_URL}/contact`} />,
      },
      {
        path: 'for-businesses',
        element: <ExternalRedirect to={`${MARKETING_SITE_URL}/for-businesses`} />,
      },
      {
        path: 'faq',
        element: <ExternalRedirect to={`${MARKETING_SITE_URL}/faq`} />,
      },
      {
        path: 'how-it-works',
        element: <ExternalRedirect to={`${MARKETING_SITE_URL}/how-it-works`} />,
      },
    ],
  },
]);

export const AppRouter = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  );
};
